import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import Phase3InterviewTracker from '../components/Phase3InterviewTracker';
// ROLE_TECH_MAP and ROLE_CATEGORIES handled inside Phase3RoleTechSelector
import Phase3RoleTechSelector from '../components/Phase3RoleTechSelector';
import Phase3QuestionSourcesPreview from '../components/Phase3QuestionSourcesPreview';
import { LEGACY_MEETINGS } from '../config/features';
import { useJobTechScreen } from '../utils/techScreen';

function MasterAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [enterpriseTab, setEnterpriseTab] = useState('schedule');
  const [searchFilter, setSearchFilter] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const [companiesList, setCompaniesList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Upload states
  const [jdFile, setJdFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Phase 3 states (Enterprise)
  const [selectedRequirement, setSelectedRequirement] = useState('');
  const [selectedRequirementTitle, setSelectedRequirementTitle] = useState('');
  const [sourcesPreview, setSourcesPreview] = useState(null);   // JD/intake sources for the selected requirement
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [hmName, setHmName] = useState('');
  const [hmEmail, setHmEmail] = useState('');
  const [roleType, setRoleType] = useState('');
  const [selectedTechStacks, setSelectedTechStacks] = useState([]);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [candidateTimezone, setCandidateTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  );
  const [interviewDuration, setInterviewDuration] = useState(60);
  const [codingMode, setCodingMode] = useState('verbal_pseudocode');
  const [techScreenFromJob, applyJobTechScreen, clearJobTechScreen] = useJobTechScreen(setInterviewDuration, setCodingMode);   // HM's technical screen (field 18)
  const [proctoringStrictness, setProctoringStrictness] = useState('medium');
  const [resume, setResume] = useState(null);
  // isLoadingPhase3Reqs removed — loading state handled inside Phase3InterviewTracker
  // phase3Requirements removed — requirements fetched inside Phase3InterviewTracker
  const [isSchedulingPhase3, setIsSchedulingPhase3] = useState(false);
  const [phase3Error, setPhase3Error] = useState('');
  const [phase3Success, setPhase3Success] = useState('');
  
  // Requirements list states
  const [requirements, setRequirements] = useState([]);
  const [isLoadingRequirements, setIsLoadingRequirements] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      
      if (tokenPayload.role !== 'master_admin') {
        console.error('Access denied: Not a master admin');
        navigate('/');
        return;
      }

      setUser({
        id: tokenPayload.user_id,
        email: tokenPayload.email,
        role: tokenPayload.role,
        full_name: tokenPayload.full_name,
        company_id: tokenPayload.company_id
      });

    } catch (error) {
      console.error('Invalid token:', error);
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchCompanies = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        setIsLoading(true);
        
        const response = await fetch(API_ENDPOINTS.COMPANIES_LIST, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          setCompaniesList(result.data || []);
        } else if (response.status === 401) {
          localStorage.clear();
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.role === 'master_admin') {
      fetchCompanies();
    }
  }, [user, navigate]);

  const fetchRequirements = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      setIsLoadingRequirements(true);
      const response = await fetch(API_ENDPOINTS.REQUIREMENTS_COMPANY_GROUPED(user.company_id), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setRequirements(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching requirements:', error);
    } finally {
      setIsLoadingRequirements(false);
    }
  }, [user]);

  // Fetch requirements when Essential tab is active
  useEffect(() => {
    if (activeMenu === 'essential' && user?.company_id) {
      fetchRequirements();
    }
  }, [activeMenu, user, fetchRequirements]);

  const handleJdFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Get file extension
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.pdf', '.doc', '.docx', '.txt'];
      const hasValidExt = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExt) {
        setUploadError('Invalid JD file type. Please upload PDF, DOC, DOCX, or TXT');
        setJdFile(null);
        return;
      }
      setJdFile(file);
      setUploadError('');
    }
  };

  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
      setUploadError('');
    }
  };

  const handleProcessFiles = async () => {
    if (!jdFile) {
      setUploadError('Please upload a Job Description file');
      return;
    }

    setIsProcessing(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const formData = new FormData();
      formData.append('jd_file', jdFile);
      if (audioFile) {
        formData.append('audio_file', audioFile);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.REQUIREMENTS_PROCESS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadSuccess(true);
        setJdFile(null);
        setAudioFile(null);
        
        // Refresh requirements list
        fetchRequirements();
        
        // Navigate to results after delay
        setTimeout(() => {
          navigate(`/view-requirement/${result.data.requirement_id}`);
        }, 1500);
      } else {
        const error = await response.json();
        setUploadError(error.detail || 'Upload failed');
      }
    } catch (error) {
      setUploadError('Connection error. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewRequirement = (requirementId) => {
    navigate(`/view-requirement/${requirementId}`);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // ── Fetch invoice summary ────────────────────────────────────────────────
  const fetchInvoiceSummary = async () => {
    setInvoiceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/invoice-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (res.ok) setInvoiceSummary(json);
    } catch(e) { console.error(e); }
    finally { setInvoiceLoading(false); }
  };

  // ── Create invoice company ────────────────────────────────────────────────
  const handleCreateInvoiceCompany = async (e) => {
    e.preventDefault();
    setInvoiceFormLoading(true);
    setInvoiceFormMsg('');
    try {
      const token = localStorage.getItem('token');
      const body = {
        company_name:           invoiceForm.company_name,
        tax_registration_number:invoiceForm.tax_registration_number || 'N/A',
        company_address:        [invoiceForm.address_line1, invoiceForm.address_line2, invoiceForm.city,
                                  invoiceForm.state_province, invoiceForm.postal_code, invoiceForm.country]
                                  .filter(Boolean).join(', '),
        admin_full_name:        invoiceForm.admin_full_name,
        admin_email:            invoiceForm.admin_email,
        admin_username:         invoiceForm.admin_username,
        admin_password:         invoiceForm.admin_password,
        invoice_phase1_rate:    invoiceForm.invoice_phase1_rate ? parseFloat(invoiceForm.invoice_phase1_rate) : null,
        invoice_phase2_rate:    invoiceForm.invoice_phase2_rate ? parseFloat(invoiceForm.invoice_phase2_rate) : null,
        invoice_phase3_rate:    invoiceForm.invoice_phase3_rate ? parseFloat(invoiceForm.invoice_phase3_rate) : null,
        invoice_contact_email:  invoiceForm.invoice_contact_email || null,
        invoice_notes:          invoiceForm.invoice_notes || null,
      };
      const res = await fetch(`${API_BASE}/api/admin/invoice-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (res.ok) {
        setInvoiceFormMsg('✅ ' + json.message);
        setInvoiceForm({ company_name:'', admin_full_name:'', admin_email:'', admin_username:'',
          admin_password:'', invoice_phase1_rate:'', invoice_phase2_rate:'',
          invoice_phase3_rate:'', invoice_contact_email:'', invoice_notes:'' });
        // Refresh company list
        const token2 = localStorage.getItem('token');
        const listRes = await fetch(`${API_BASE}/api/companies/list`, { headers: { Authorization: `Bearer ${token2}` }});
        const listJson = await listRes.json();
        if (listRes.ok) setCompaniesList(listJson.data || []);
        setTimeout(() => { setShowCreateInvoice(false); setInvoiceFormMsg(''); }, 2000);
      } else {
        setInvoiceFormMsg('❌ ' + (json.detail || 'Failed to create company'));
      }
    } catch(e) { setInvoiceFormMsg('❌ Connection error'); }
    finally { setInvoiceFormLoading(false); }
  };

  // ── Create support user ───────────────────────────────────────────────────
  const handleCreateSupportUser = async (e) => {
    e.preventDefault();
    setSupportFormLoading(true);
    setSupportFormMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/support-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(supportForm),
      });
      const json = await res.json();
      if (res.ok) {
        setSupportFormMsg('✅ ' + json.message);
        setSupportForm({ full_name:'', email:'', username:'', password:'' });
        setTimeout(() => { setShowCreateSupport(false); setSupportFormMsg(''); }, 2000);
      } else {
        setSupportFormMsg('❌ ' + (json.detail || 'Failed'));
      }
    } catch(e) { setSupportFormMsg('❌ Connection error'); }
    finally { setSupportFormLoading(false); }
  };

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
  };

  // Subscription MRR — only subscription billing companies
  const subscriptionCompanies = companiesList.filter(c => !c.billing_type || c.billing_type === 'subscription');
  const totalRevenue = subscriptionCompanies.reduce((sum, c) => {
  const prices = { essential: 99, professional: 249, enterprise: 499 };
    return sum + (prices[c.subscription_plan] || 0);
  }, 0);

  const activePlans = {
    essential: companiesList.filter(c => c.subscription_plan === 'essential' && c.is_active).length,
    professional: companiesList.filter(c => c.subscription_plan === 'professional' && c.is_active).length,
    enterprise: companiesList.filter(c => c.subscription_plan === 'enterprise' && c.is_active).length
  };

  // ── Invoice clients state ─────────────────────────────────────────────────
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // ── Create Invoice Company modal state ────────────────────────────────────
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    company_name: '', tax_registration_number: '',
    address_line1: '', address_line2: '', city: '', state_province: '', postal_code: '', country: '',
    admin_full_name: '', admin_email: '', admin_username: '', admin_password: '',
    invoice_phase1_rate: '', invoice_phase2_rate: '', invoice_phase3_rate: '',
    invoice_contact_email: '', invoice_notes: ''
  });
  const [invoiceFormLoading, setInvoiceFormLoading] = useState(false);
  const [invoiceFormMsg, setInvoiceFormMsg] = useState('');

  // ── Create Support User modal state ───────────────────────────────────────
  const [showCreateSupport, setShowCreateSupport] = useState(false);
  const [supportForm, setSupportForm] = useState({
    full_name: '', email: '', username: '', password: ''
  });
  const [supportFormLoading, setSupportFormLoading] = useState(false);
  const [supportFormMsg, setSupportFormMsg] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const filteredCompanies = companiesList.filter(company => {
    const matchesSearch = company.company_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                          company.company_id.toLowerCase().includes(searchFilter.toLowerCase());
    const matchesPlan = filterPlan === 'all' || company.subscription_plan === filterPlan;
    return matchesSearch && matchesPlan;
  });

  // FIXED handleSchedulePhase3 function
// Replace the old handleSchedulePhase3 in all 3 dashboards with this

const handleSchedulePhase3 = async () => {
  // Validation
  if (!selectedRequirement) { setPhase3Error('Please select a requirement'); return; }
  if (!candidateName.trim()) { setPhase3Error('Please enter candidate name'); return; }
  if (!candidateEmail.trim()) { setPhase3Error('Please enter candidate email'); return; }
  if (!interviewDate) { setPhase3Error('Please select interview date'); return; }
  if (!interviewTime) { setPhase3Error('Please select interview time'); return; }
  if (!candidateTimezone) { setPhase3Error('Please select candidate timezone'); return; }
  if (!roleType) { setPhase3Error('Please select role type'); return; }
  if (selectedTechStacks.length === 0) { setPhase3Error('Please select at least one tech stack'); return; }
  if (!resume) { setPhase3Error('Please upload resume (PDF, DOCX, DOC, or TXT)'); return; }
  
  // Validate email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(candidateEmail)) { setPhase3Error('Please enter a valid email address'); return; }

  setIsSchedulingPhase3(true);
  setPhase3Error('');
  setPhase3Success('');

  try {
    const formData = new FormData();
    const token = localStorage.getItem('token');
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    
    formData.append('company_id', tokenPayload.company_id);
    formData.append('requirement_id', selectedRequirement);
    formData.append('candidate_email', candidateEmail);
    formData.append('candidate_name', candidateName);
    formData.append('role_type', roleType);
    formData.append('tech_stack', selectedTechStacks.join(', '));
    formData.append('scheduled_datetime', `${interviewDate}T${interviewTime}`);
    formData.append('candidate_timezone', candidateTimezone);
    formData.append('duration_minutes', interviewDuration);
    formData.append('coding_mode', codingMode);
    formData.append('proctoring_strictness', proctoringStrictness);
    if (hmName.trim()) formData.append('hiring_manager_name', hmName.trim());
    if (hmEmail.trim()) formData.append('hiring_manager_email', hmEmail.trim());
    formData.append('resume', resume);

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/phase3/schedule-interview`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    const result = await response.json();

    if (response.ok) {
      setPhase3Success(result.message || 'Interview scheduled successfully!');
      setSelectedRequirement(''); clearJobTechScreen();
      setSelectedRequirementTitle('');
      setCandidateName('');
      setCandidateEmail('');
      setHmName('');
      setHmEmail('');
      setRoleType('');
      setSelectedTechStacks([]);
      setInterviewDate('');
      setInterviewTime('');
      setInterviewDuration(60);
      setCodingMode('verbal_pseudocode');
      setProctoringStrictness('medium');
      setResume(null);
      
      const fileInput = document.getElementById('phase3-resume');
      if (fileInput) fileInput.value = '';
      
      setTimeout(() => { setActiveMenu('dashboard'); setPhase3Success(''); }, 2000);
    } else {
      // FIX: Handle Pydantic validation errors properly
      let errorMsg = 'Failed to schedule interview';
      
      if (result.detail) {
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(result.detail)) {
          errorMsg = result.detail.map(err => err.msg).join(', ');
        } 
        // If detail is a string
        else if (typeof result.detail === 'string') {
          errorMsg = result.detail;
        }
      }
      
      setPhase3Error(errorMsg);
    }
  } catch (error) {
    console.error('Schedule error:', error);
    setPhase3Error('Connection error. Please try again.');
  } finally {
    setIsSchedulingPhase3(false);
  }
};

  const renderContent = () => {
    switch (activeMenu) {
      case 'essential':
        return renderEssential();
      case 'professional':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Professional Plan</h1>
              <p className="text-sm text-gray-600 mt-0.5">Schedule AI-powered intake meetings with hiring managers</p>
            </div>

            {/* Schedule Meeting Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-12 text-center">
              <div className="inline-block bg-white rounded-full p-6 shadow-lg mb-6">
                <div className="text-6xl">💼</div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">AI-Powered Requirement Briefing</h2>
              <p className="text-gray-700 mb-2 text-lg font-medium">Schedule Intake Meetings</p>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                AI will conduct professional intake calls with hiring managers at scheduled times. 
                After the meeting, AI processes the conversation and generates recruiter documents and screening questions.
              </p>
              {LEGACY_MEETINGS && <button
                onClick={() => navigate('/schedule-meeting')}
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-brand-blue to-blue-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-brand-blue transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <span className="text-2xl">📅</span>
                <span>Schedule AI Meeting</span>
              </button>}
            </div>

            {/* Features */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="text-4xl mb-4">🤖</div>
                <h3 className="font-bold text-gray-900 mb-2">AI-Conducted Calls</h3>
                <p className="text-sm text-gray-600">AI conducts professional intake calls with hiring managers</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="text-4xl mb-4">📄</div>
                <h3 className="font-bold text-gray-900 mb-2">Auto Documentation</h3>
                <p className="text-sm text-gray-600">Generates recruiter-friendly documents automatically</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="text-4xl mb-4">✅</div>
                <h3 className="font-bold text-gray-900 mb-2">Screening Questions</h3>
                <p className="text-sm text-gray-600">Creates tailored screening questions for candidates</p>
              </div>
            </div>
          </div>
        );
      case 'enterprise':
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Enterprise Plan</h1>
        <p className="text-sm text-gray-600 mt-0.5">AI-powered technical interviews</p>
      </div>

      {/* Enterprise Sub-tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-2xl w-fit">
        <button
          onClick={() => setEnterpriseTab('schedule')}
          className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            enterpriseTab === 'schedule'
              ? 'bg-white text-purple-700 shadow-sm border border-purple-200'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📋 Schedule Interview
        </button>
      </div>

            {/* Schedule Interview Tab */}
      {enterpriseTab === 'schedule' && (
      <div>

      {phase3Success && (
        <div className="bg-green-50 border-2 border-green-400 rounded-2xl p-5 flex items-center gap-3">
          <span className="text-2xl">✅</span>
          <p className="text-green-700 font-bold">{phase3Success}</p>
        </div>
      )}
      {phase3Error && (
        <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-5 flex items-center gap-3">
          <span className="text-2xl">❌</span>
          <p className="text-red-700 font-bold">{phase3Error}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <h2 className="text-lg font-bold text-gray-900 mb-6">Schedule Phase 3 Interview</h2>
        <div className="space-y-6">
          
          {/* Step 1: Select Requirement */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Job Requirement *</label>
            {isLoadingRequirements ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-blue border-t-transparent"></div>
                Loading requirements...
              </div>
            ) : requirements && requirements.length === 0 ? (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-200">
                No requirements found. Please create one in Phase 1 (Essential) first.
              </div>
            ) : (
              <select
                value={selectedRequirement}
                onChange={(e) => {
                  const reqId = e.target.value;
                  setSelectedRequirement(reqId);
                  let title = '';
                  let foundHmName = '';
                  if (reqId && requirements) {
                    const found = requirements.find(r => String(r.id) === reqId);
                    applyJobTechScreen(found && found.tech_screen);
                    if (found) {
                      title = found.jobTitle || found.job_title || '';
                      foundHmName = found.hiringManagerName || '';
                    }
                  }
                  setSelectedRequirementTitle(title);
                  setHmName(foundHmName);
                  setHmEmail('');
                  setRoleType('');
                  setSelectedTechStacks([]);
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              >
                <option value="">-- Select a requirement --</option>
                {requirements && requirements.map((req) => (
                  <option key={req.id} value={req.id}>
                    {req.jobTitle}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Step 2: Candidate Name & Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Candidate Name *</label>
              <input
                type="text"
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
                placeholder="e.g., John Doe"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                value={candidateEmail}
                onChange={(e) => setCandidateEmail(e.target.value)}
                placeholder="john@example.com"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              />
            </div>
          </div>

          {/* Step 2b: Hiring Manager (auto-populated from requirement) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Hiring Manager Name
                {hmName && <span className="ml-2 text-xs font-normal text-green-600">✓ From requirement</span>}
              </label>
              <input
                type="text"
                value={hmName}
                onChange={(e) => setHmName(e.target.value)}
                placeholder="Auto-filled from requirement or enter manually"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all ${
                  hmName ? 'border-green-300 bg-green-50' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hiring Manager Email</label>
              <input
                type="email"
                value={hmEmail}
                onChange={(e) => setHmEmail(e.target.value)}
                placeholder="hiring.manager@company.com"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              />
            </div>
          </div>


          {/* Step 3b: Role Type & Tech Stack (Modern AI-Matched Selector) */}
          <Phase3RoleTechSelector
            roleType={roleType}
            setRoleType={setRoleType}
            selectedTechStacks={selectedTechStacks}
            setSelectedTechStacks={setSelectedTechStacks}
            selectedRequirementTitle={selectedRequirementTitle}
            suggestedTechs={String(sourcesPreview?.requirement_id) === String(selectedRequirement) ? sourcesPreview.must_have_technologies : null}
            suggestionKey={selectedRequirement}
          />

          {/* Which JD + intake the questions come from (and warnings) */}
          <Phase3QuestionSourcesPreview requirementId={selectedRequirement} techStack={selectedTechStacks.join(', ')}
                                        onSources={setSourcesPreview} />

          {/* Step 3c: Interview Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Duration *</label>
              <select
                value={interviewDuration}
                onChange={(e) => setInterviewDuration(Number(e.target.value))}
              disabled={techScreenFromJob}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              >
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes (recommended)</option>
                <option value={75}>75 minutes</option>
              </select>
            {techScreenFromJob && <p className="text-xs text-gray-500 mt-1" data-testid="set-by-hm">Set by hiring manager</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Coding Mode *</label>
              <select
                value={codingMode}
                onChange={(e) => setCodingMode(e.target.value)}
              disabled={techScreenFromJob}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              >
                <option value="verbal_pseudocode">Verbal + Pseudocode</option>
                <option value="live_coding">Live Coding</option>
              </select>
            {techScreenFromJob && <p className="text-xs text-gray-500 mt-1" data-testid="set-by-hm">Set by hiring manager</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Proctoring Strictness *</label>
              <select
                value={proctoringStrictness}
                onChange={(e) => setProctoringStrictness(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              >
                <option value="low">Low (minimal monitoring)</option>
                <option value="medium">Medium (recommended)</option>
                <option value="high">High (strict monitoring)</option>
              </select>
            </div>
          </div>

          {/* Step 4: Resume Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Resume (PDF, DOCX, or TXT) *</label>
            <input
              id="phase3-resume"
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const ext = file.name.split('.').pop().toLowerCase();
                  if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
                    setResume(file);
                    setPhase3Error('');
                  } else {
                    setPhase3Error('Resume must be PDF, DOC, DOCX, or TXT');
                  }
                }
              }}
              className="hidden"
            />
            <label
              htmlFor="phase3-resume"
              className={`flex flex-col items-center justify-center w-full h-32 px-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                resume ? 'border-brand-green bg-green-50' : 'border-gray-300 hover:border-brand-blue hover:bg-blue-50'
              }`}
            >
              <span className="text-3xl mb-2">📄</span>
              <span className="text-sm text-gray-600 font-medium">
                {resume ? resume.name : 'Click to upload resume (PDF, DOCX, or TXT)'}
              </span>
            </label>
          </div>

          {/* Step 5: Candidate Timezone, Date and Time */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🌐 Candidate's Timezone *
              <span className="ml-2 text-xs font-normal text-gray-500">The date and time below are in this timezone</span>
            </label>
            <select
              value={candidateTimezone}
              onChange={(e) => setCandidateTimezone(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all bg-white"
            >
              <optgroup label="🇺🇸 United States">
                <option value="America/New_York">Eastern Time (ET) — New York, Miami, Atlanta</option>
                <option value="America/Chicago">Central Time (CT) — Chicago, Dallas, Houston</option>
                <option value="America/Denver">Mountain Time (MT) — Denver, Salt Lake City</option>
                <option value="America/Phoenix">Mountain Time (no DST) — Phoenix, Arizona</option>
                <option value="America/Los_Angeles">Pacific Time (PT) — Los Angeles, Seattle, SF</option>
                <option value="America/Anchorage">Alaska Time (AKT) — Anchorage</option>
                <option value="Pacific/Honolulu">Hawaii Time (HST) — Honolulu</option>
              </optgroup>
              <optgroup label="🇨🇦 Canada">
                <option value="America/Toronto">Eastern Time — Toronto, Ottawa</option>
                <option value="America/Vancouver">Pacific Time — Vancouver</option>
                <option value="America/Edmonton">Mountain Time — Calgary, Edmonton</option>
                <option value="America/Halifax">Atlantic Time — Halifax</option>
              </optgroup>
              <optgroup label="🇬🇧 🇮🇪 UK & Ireland">
                <option value="Europe/London">GMT / BST — London, Dublin</option>
              </optgroup>
              <optgroup label="🇪🇺 Europe">
                <option value="Europe/Paris">Central European Time — Paris, Berlin, Rome, Madrid</option>
                <option value="Europe/Amsterdam">Central European Time — Amsterdam, Brussels</option>
                <option value="Europe/Stockholm">Central European Time — Stockholm, Oslo, Copenhagen</option>
                <option value="Europe/Warsaw">Central European Time — Warsaw, Prague, Vienna</option>
                <option value="Europe/Helsinki">Eastern European Time — Helsinki, Tallinn, Riga</option>
                <option value="Europe/Bucharest">Eastern European Time — Bucharest, Sofia</option>
                <option value="Europe/Athens">Eastern European Time — Athens</option>
                <option value="Europe/Moscow">Moscow Time (MSK) — Moscow, St. Petersburg</option>
              </optgroup>
              <optgroup label="🌍 Middle East & Africa">
                <option value="Asia/Dubai">Gulf Standard Time — Dubai, Abu Dhabi</option>
                <option value="Asia/Riyadh">Arabia Standard Time — Riyadh, Kuwait</option>
                <option value="Asia/Jerusalem">Israel Time — Tel Aviv, Jerusalem</option>
                <option value="Africa/Cairo">Egypt Standard Time — Cairo</option>
                <option value="Africa/Johannesburg">South Africa Time — Johannesburg, Cape Town</option>
                <option value="Africa/Lagos">West Africa Time — Lagos, Accra</option>
                <option value="Africa/Nairobi">East Africa Time — Nairobi</option>
              </optgroup>
              <optgroup label="🇮🇳 South Asia">
                <option value="Asia/Kolkata">India Standard Time (IST) — Mumbai, Bangalore, Delhi</option>
                <option value="Asia/Karachi">Pakistan Standard Time — Karachi, Lahore, Islamabad</option>
                <option value="Asia/Dhaka">Bangladesh Standard Time — Dhaka</option>
                <option value="Asia/Colombo">Sri Lanka Time — Colombo</option>
                <option value="Asia/Kathmandu">Nepal Time — Kathmandu</option>
              </optgroup>
              <optgroup label="🌏 East & Southeast Asia">
                <option value="Asia/Shanghai">China Standard Time (CST) — Beijing, Shanghai, Shenzhen</option>
                <option value="Asia/Hong_Kong">Hong Kong Time — Hong Kong</option>
                <option value="Asia/Taipei">Taiwan Time — Taipei</option>
                <option value="Asia/Tokyo">Japan Standard Time (JST) — Tokyo, Osaka</option>
                <option value="Asia/Seoul">Korea Standard Time — Seoul</option>
                <option value="Asia/Singapore">Singapore Time (SGT) — Singapore</option>
                <option value="Asia/Kuala_Lumpur">Malaysia Time — Kuala Lumpur</option>
                <option value="Asia/Bangkok">Indochina Time — Bangkok, Ho Chi Minh City</option>
                <option value="Asia/Manila">Philippines Time — Manila</option>
                <option value="Asia/Jakarta">Western Indonesia Time — Jakarta</option>
              </optgroup>
              <optgroup label="🇦🇺 Australia & Pacific">
                <option value="Australia/Sydney">AEST / AEDT — Sydney, Melbourne, Brisbane</option>
                <option value="Australia/Adelaide">ACST / ACDT — Adelaide</option>
                <option value="Australia/Perth">AWST — Perth</option>
                <option value="Pacific/Auckland">New Zealand Time — Auckland, Wellington</option>
              </optgroup>
              <optgroup label="🌎 Latin America">
                <option value="America/Sao_Paulo">Brazil Time — São Paulo, Rio de Janeiro</option>
                <option value="America/Argentina/Buenos_Aires">Argentina Time — Buenos Aires</option>
                <option value="America/Mexico_City">Mexico City Time — Mexico City, Guadalajara</option>
                <option value="America/Bogota">Colombia Time — Bogotá, Medellín</option>
                <option value="America/Santiago">Chile Time — Santiago</option>
                <option value="America/Lima">Peru Time — Lima</option>
              </optgroup>
              <optgroup label="🌐 UTC">
                <option value="UTC">UTC — Coordinated Universal Time</option>
              </optgroup>
            </select>
            {candidateTimezone && (
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span>🕐 Current time in candidate's timezone:</span>
                <span className="font-semibold text-gray-700">
                  {new Date().toLocaleTimeString('en-US', {
                    timeZone: candidateTimezone,
                    hour: '2-digit', minute: '2-digit', hour12: true
                  })}
                  {' '}
                  ({new Intl.DateTimeFormat('en-US', { timeZone: candidateTimezone, timeZoneName: 'short' })
                    .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value})
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Date *</label>
              <input
                type="date"
                value={interviewDate}
                onChange={(e) => setInterviewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Time *</label>
              <input
                type="time"
                value={interviewTime}
                onChange={(e) => setInterviewTime(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
              />
            </div>
          </div>
          {interviewDate && interviewTime && candidateTimezone && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-start gap-2">
              <span className="text-indigo-500 text-lg mt-0.5">📅</span>
              <div>
                <p className="text-sm font-semibold text-indigo-800">Candidate will be invited to join at:</p>
                <p className="text-sm text-indigo-700 mt-0.5">
                  {new Date(`${interviewDate}T${interviewTime}`).toLocaleDateString('en-US', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                  {' at '}
                  {new Date(`${interviewDate}T${interviewTime}`).toLocaleTimeString('en-US', {
                    hour: '2-digit', minute: '2-digit', hour12: true
                  })}
                  {' '}
                  <span className="font-bold">
                    ({new Intl.DateTimeFormat('en-US', { timeZone: candidateTimezone, timeZoneName: 'short' })
                      .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value})
                  </span>
                </p>
                <p className="text-xs text-indigo-500 mt-1">Stored as UTC. Email invite will show this time in the candidate's timezone.</p>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800 font-semibold mb-1">How it works:</p>
            <p className="text-sm text-blue-700">
              Candidate receives secure email with interview link. ElevenLabs AI conducts technical interview with questions tailored to the requirement. Interview is recorded and scored by Claude AI. Results delivered within 5 minutes.
            </p>
          </div>

          {/* Schedule Button */}
          <button
            onClick={handleSchedulePhase3}
            disabled={isSchedulingPhase3 || (requirements && requirements.length === 0)}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-lg"
          >
            {isSchedulingPhase3 ? (
              <span className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Scheduling...
              </span>
            ) : (
              '✓ Schedule Interview →'
            )}
          </button>
        </div>
      </div>

      {/* Feature Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="text-4xl mb-4">🤖</div>
          <h3 className="font-bold text-gray-900 mb-2">AI-Conducted Interviews</h3>
          <p className="text-sm text-gray-600">ElevenLabs AI conducts real technical interviews with adaptive questions</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="text-4xl mb-4">⭐</div>
          <h3 className="font-bold text-gray-900 mb-2">Smart Scoring</h3>
          <p className="text-sm text-gray-600">Claude AI scores across 5 dimensions with percentile rankings vs cohort</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="font-bold text-gray-900 mb-2">Detailed Reports</h3>
          <p className="text-sm text-gray-600">Hiring manager-friendly reports with candidate feedback and recommendations</p>
        </div>
      </div>
      </div>
      )}
    </div>
  );
      default:
        return renderDashboard();
    }
  };

  const renderEssential = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Essential Plan</h1>
        <p className="text-sm text-gray-600 mt-0.5">Upload job descriptions and process requirements</p>
      </div>

      {/* Messages */}
      {uploadSuccess && (
        <div className="bg-green-50 border-l-4 border-green-400 text-green-700 px-4 py-3 rounded text-sm">
          ✓ Files processed successfully! Redirecting...
        </div>
      )}

      {uploadError && (
        <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded text-sm">
          ✕ {uploadError}
        </div>
      )}

      {/* Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Upload Files</h2>
        
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* JD Upload */}
          <div>
            <input
              type="file"
              id="jd-upload"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleJdFileChange}
              className="hidden"
            />
            <label
              htmlFor="jd-upload"
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-brand-blue transition-colors cursor-pointer block"
            >
              <p className="text-4xl mb-4">📄</p>
              <p className="font-semibold text-gray-900 mb-2">Upload Job Description</p>
              <p className="text-sm text-gray-600">PDF, DOC, DOCX, or TXT</p>
              {jdFile && (
                <p className="text-xs text-green-600 font-semibold mt-4">✓ {jdFile.name}</p>
              )}
              {!jdFile && (
                <p className="text-xs text-gray-500 mt-4">Click to upload</p>
              )}
            </label>
          </div>

          {/* Audio Upload */}
          <div>
            <input
              type="file"
              id="audio-upload"
              accept="audio/*,video/*,.mp3,.wav,.m4a,.mp4,.mov,.avi"
              onChange={handleAudioFileChange}
              className="hidden"
            />
            <label
              htmlFor="audio-upload"
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-brand-blue transition-colors cursor-pointer block"
            >
              <p className="text-4xl mb-4">🎙️</p>
              <p className="font-semibold text-gray-900 mb-2">Upload Audio/Video</p>
              <p className="text-sm text-gray-600">MP3, WAV, MP4, etc. (Optional)</p>
              {audioFile && (
                <p className="text-xs text-green-600 font-semibold mt-4">✓ {audioFile.name}</p>
              )}
              {!audioFile && (
                <p className="text-xs text-gray-500 mt-4">Click to upload</p>
              )}
            </label>
          </div>
        </div>

        <button
          onClick={handleProcessFiles}
          disabled={isProcessing || !jdFile}
          className="w-full px-6 py-3 bg-brand-blue text-white font-semibold rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isProcessing ? '⏳ Processing Files...' : 'Process Files →'}
        </button>
      </div>

      {/* Requirements List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Your Requirements</h2>
          <p className="text-sm text-gray-600 mt-0.5">View processed job descriptions</p>
        </div>

        {isLoadingRequirements ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent mx-auto"></div>
            <p className="text-gray-600 mt-4 text-sm">Loading...</p>
          </div>
        ) : requirements.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-5xl mb-3">📭</div>
            <p className="text-gray-600 text-sm">No requirements yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase">Job Title</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase">Quality</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requirements.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 text-sm">{req.job_title}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(req.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 rounded-full h-2"
                            style={{ width: `${req.quality_score || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-700">
                          {req.quality_score || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        Completed
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewRequirement(req.id)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-brand-blue hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        👁️ View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderDashboard = () => (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg p-6 border border-blue-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-blue-700 text-sm font-semibold uppercase tracking-wide">Total Companies</span>
            <div className="bg-blue-500 rounded-full p-2">
              <span className="text-2xl">🏢</span>
            </div>
          </div>
          <div className="text-4xl font-bold text-blue-900 mb-1">{companiesList.length}</div>
          <div className="text-sm text-blue-600 font-medium">
            {companiesList.filter(c => c.is_active).length} active
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-lg p-6 border border-green-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-green-700 text-sm font-semibold uppercase tracking-wide">Monthly Revenue</span>
            <div className="bg-green-500 rounded-full p-2">
              <span className="text-2xl">💰</span>
            </div>
          </div>
          <div className="text-4xl font-bold text-green-900 mb-1">${totalRevenue}</div>
          <div className="text-sm text-green-600 font-medium">From subscriptions</div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-lg p-6 border border-purple-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-purple-700 text-sm font-semibold uppercase tracking-wide">Essential Plans</span>
            <div className="bg-purple-500 rounded-full p-2">
              <span className="text-2xl">📦</span>
            </div>
          </div>
          <div className="text-4xl font-bold text-purple-900 mb-1">{activePlans.essential}</div>
          <div className="text-sm text-purple-600 font-medium">$99/month each</div>
        </div>

        <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl shadow-lg p-6 border border-pink-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-pink-700 text-sm font-semibold uppercase tracking-wide">Pro + Enterprise</span>
            <div className="bg-pink-500 rounded-full p-2">
              <span className="text-2xl">🚀</span>
            </div>
          </div>
          <div className="text-4xl font-bold text-pink-900 mb-1">
            {activePlans.professional + activePlans.enterprise}
          </div>
          <div className="text-sm text-pink-600 font-medium">
            {activePlans.professional} Pro, {activePlans.enterprise} Enterprise
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 mb-6">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
            <input
              type="text"
              placeholder="Search by company name or ID..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            />
          </div>
          <select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            className="px-6 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue font-medium transition-all"
          >
            <option value="all">All Plans</option>
            <option value="essential">Essential</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
      </div>

      {/* Companies Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-16 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-blue border-t-transparent mx-auto"></div>
              <p className="text-gray-600 mt-6 font-medium">Loading companies...</p>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="p-16 text-center">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-600 text-lg font-medium">No companies found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-bold text-gray-900 uppercase tracking-wide">Company</th>
                  <th className="text-left px-6 py-4 text-sm font-bold text-gray-900 uppercase tracking-wide">Plan</th>
                  <th className="text-left px-6 py-4 text-sm font-bold text-gray-900 uppercase tracking-wide">Usage</th>
                  <th className="text-left px-6 py-4 text-sm font-bold text-gray-900 uppercase tracking-wide">Revenue</th>
                  <th className="text-left px-6 py-4 text-sm font-bold text-gray-900 uppercase tracking-wide">Registered</th>
                  <th className="text-left px-6 py-4 text-sm font-bold text-gray-900 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCompanies.map((company) => {
                  const planColors = {
                    essential: 'bg-blue-100 text-blue-800 border-blue-200',
                    professional: 'bg-green-100 text-green-800 border-green-200',
                    enterprise: 'bg-purple-100 text-purple-800 border-purple-200'
                  };
                  const planPrices = { essential: 99, professional: 249, enterprise: 499 };

                  return (
                    <tr key={company.id} className="hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-bold text-gray-900">{company.company_name}</div>
                          <div className="text-sm text-gray-500 font-medium">{company.company_id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase border ${planColors[company.subscription_plan]}`}>
                          {company.subscription_plan}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-brand-blue to-blue-600 rounded-full h-3 transition-all"
                              style={{ width: `${Math.min((company.requirements_used / company.monthly_limit) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-700 font-semibold whitespace-nowrap">
                            {company.requirements_used}/{company.monthly_limit}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-900 text-lg">
                          ${planPrices[company.subscription_plan]}
                        </div>
                        <div className="text-xs text-gray-500">per month</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                        {new Date(company.registration_date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold border ${
                          company.is_active
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : 'bg-red-100 text-red-800 border-red-200'
                        }`}>
                          {company.is_active ? '✓ Active' : '✕ Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {/* My Interviews — Phase 3 */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mt-2">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h2 className="text-xl font-bold text-gray-900">AI Technical Interviews</h2>
            <p className="text-sm text-gray-500 mt-0.5">All scheduled and completed candidate interviews</p>
          </div>
        </div>
        <div className="p-6">
          <Phase3InterviewTracker userRole={user?.role} />
        </div>
      </div>

      {/* ── Invoice Clients Section ───────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mt-6">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📑</span>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Invoice Clients</h2>
              <p className="text-sm text-gray-400 mt-0.5">Manual billing — usage × negotiated rates</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { fetchInvoiceSummary(); }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl transition-all"
            >
              📊 Load This Month
            </button>
            <button
              onClick={() => setShowCreateInvoice(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-xl transition-all"
            >
              + Add Invoice Client
            </button>
          </div>
        </div>

        {invoiceLoading ? (
          <div className="p-10 text-center text-gray-400">Loading invoice data...</div>
        ) : !invoiceSummary ? (
          <div className="p-10 text-center text-gray-400">
            <div className="text-4xl mb-3">📑</div>
            <p className="text-sm">Click "Load This Month" to see invoice summary</p>
          </div>
        ) : invoiceSummary.data.length === 0 ? (
          <div className="p-10 text-center text-gray-400">
            <p className="text-sm">No invoice clients yet. Click "Add Invoice Client" to create one.</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Total estimated row */}
            <div className="flex items-center justify-between mb-5 p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div>
                <p className="text-xs text-purple-500 font-bold uppercase tracking-wide">Total Estimated — {invoiceSummary.month}</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">${invoiceSummary.total_estimated.toLocaleString()}</p>
              </div>
              <span className="text-xs text-purple-400 font-medium">{invoiceSummary.company_count} client{invoiceSummary.company_count !== 1 ? 's' : ''}</span>
            </div>

            {/* Per-company breakdown */}
            <div className="space-y-3">
              {invoiceSummary.data.map((c, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition-all">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{c.company_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{c.invoice_contact_email}</p>
                      {c.invoice_notes && <p className="text-xs text-gray-400 italic mt-0.5">{c.invoice_notes}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-purple-700">${c.invoice_amount.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{c.month}</p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    {[
                      { label: 'Phase 1 (JD)', count: c.usage.phase1, rate: c.rates.phase1 },
                      { label: 'Phase 2 (Intake)', count: c.usage.phase2, rate: c.rates.phase2 },
                      { label: 'Phase 3 (Interview)', count: c.usage.phase3, rate: c.rates.phase3 },
                    ].map((ph, j) => (
                      <div key={j} className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] text-gray-400 font-medium">{ph.label}</p>
                        <p className="text-base font-bold text-gray-800 mt-0.5">{ph.count}</p>
                        <p className="text-[10px] text-gray-400">${ph.rate || '—'} ea</p>
                        {ph.rate && ph.count > 0 && (
                          <p className="text-[10px] text-purple-600 font-semibold">${(ph.rate * ph.count).toFixed(2)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>



    </>
  );

  // eslint-disable-next-line no-unused-vars
  const renderWorkInProgress = (planName) => (
    <div className="text-center p-20">
      <div className="inline-block bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full p-8 mb-6 shadow-lg">
        <div className="text-8xl">🚧</div>
      </div>
      <h2 className="text-3xl font-bold text-gray-900 mb-3">{planName} Plan</h2>
      <p className="text-xl text-gray-600 font-medium">Work in Progress</p>
      <p className="text-gray-500 mt-2">This feature is under development</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-gray-200 flex flex-col shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <img src="/Reqt5d-Logo.png" alt="Quali5d" className="h-14" />
        </div>

        <nav className="flex-1 px-4 py-6 space-y-2">
          <button
            onClick={() => handleMenuClick('dashboard')}
            className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl text-left font-semibold transition-all transform hover:scale-105 ${
              activeMenu === 'dashboard' 
                ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}>
            <span className="text-2xl">📊</span>
            <span>Dashboard</span>
          </button>

          {LEGACY_MEETINGS && <button
            onClick={() => window.location.href = '/my-meetings'}
            className="w-full flex items-center gap-4 px-5 py-4 rounded-xl text-left font-semibold transition-all transform hover:scale-105 text-gray-700 hover:bg-gray-100">
            <span className="text-2xl">📅</span>
            <span>My Meetings</span>
          </button>}

          <button
            onClick={() => window.location.href = '/my-interviews'}
            className="w-full flex items-center gap-4 px-5 py-4 rounded-xl text-left font-semibold transition-all transform hover:scale-105 text-gray-700 hover:bg-gray-100">
            <span className="text-2xl">🎙️</span>
            <span>My Interviews</span>
          </button>

          {LEGACY_MEETINGS && <button
            onClick={() => handleMenuClick('essential')}
            className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl text-left font-semibold transition-all transform hover:scale-105 ${
              activeMenu === 'essential' 
                ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}>
            <span className="text-2xl">📦</span>
            <span>Essential</span>
          </button>}

          {LEGACY_MEETINGS && <button
            onClick={() => handleMenuClick('professional')}
            className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl text-left font-semibold transition-all transform hover:scale-105 ${
              activeMenu === 'professional' 
                ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}>
            <span className="text-2xl">💼</span>
            <span>Professional</span>
          </button>}

          <button
            onClick={() => handleMenuClick('enterprise')}
            className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl text-left font-semibold transition-all transform hover:scale-105 ${
              activeMenu === 'enterprise' 
                ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}>
            <span className="text-2xl">🚀</span>
            <span>Enterprise</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header with Profile Dropdown */}
        <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-brand-blue to-blue-600 bg-clip-text text-transparent">
                Master Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1 font-medium">Manage all companies and subscriptions</p>
            </div>
            
            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-3 px-4 py-2 rounded-xl hover:bg-gray-50 transition-all group"
              >
                <div className="text-right">
                  <div className="text-xs text-gray-500 font-medium">Logged in as</div>
                  <div className="font-bold text-gray-900">{user?.full_name || 'Master Admin'}</div>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:shadow-xl transition-shadow">
                  {user?.full_name?.charAt(0) || 'M'}
                </div>
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 animate-fadeIn">
                  <button
                    onClick={() => {
                      navigate('/manage-users');
                      setDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-blue-50 transition-colors font-medium"
                  >
                    <span className="text-xl">👥</span>
                    <span>Manage Users</span>
                  </button>
                  
                  <div className="border-t border-gray-100 my-1"></div>
                  <div className="px-4 py-1.5">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Create</p>
                  </div>

                  <button
                    onClick={() => { setShowCreateInvoice(true); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-700 hover:bg-purple-50 transition-colors font-medium"
                  >
                    <span className="text-xl">🏢</span>
                    <span>New Invoice Client</span>
                  </button>

                  <button
                    onClick={() => { setShowCreateSupport(true); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-700 hover:bg-purple-50 transition-colors font-medium"
                  >
                    <span className="text-xl">🎧</span>
                    <span>New Support User</span>
                  </button>

                  <div className="border-t border-gray-100 my-1"></div>
                  <div className="px-4 py-1.5">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Account</p>
                  </div>

                  <button
                    onClick={() => {
                      navigate('/settings');
                      setDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-700 hover:bg-blue-50 transition-colors font-medium"
                  >
                    <span className="text-xl">⚙️</span>
                    <span>Settings</span>
                  </button>
                  
                  <div className="border-t border-gray-200 my-2"></div>
                  
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors font-medium"
                  >
                    <span className="text-xl">🚪</span>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="p-8">
          {renderContent()}
        </div>
      </main>

      {/* ── Modals ── */}
      <CreateInvoiceModal
        show={showCreateInvoice}
        onClose={() => { setShowCreateInvoice(false); setInvoiceFormMsg(''); }}
        form={invoiceForm}
        setForm={setInvoiceForm}
        onSubmit={handleCreateInvoiceCompany}
        loading={invoiceFormLoading}
        message={invoiceFormMsg}
      />
      <CreateSupportModal
        show={showCreateSupport}
        onClose={() => { setShowCreateSupport(false); setSupportFormMsg(''); }}
        form={supportForm}
        setForm={setSupportForm}
        onSubmit={handleCreateSupportUser}
        loading={supportFormLoading}
        message={supportFormMsg}
      />
    </div>
  );
}

// ── Create Invoice Company Modal ────────────────────────────────────────────
function CreateInvoiceModal({ show, onClose, form, setForm, onSubmit, loading, message }) {
  if (!show) return null;

  const inp  = "w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-sm";
  const inp2 = "w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-sm bg-gray-50";
  const lbl  = "block text-sm font-semibold text-gray-700 mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create Invoice Client</h2>
            <p className="text-sm text-gray-400 mt-0.5">Enterprise plan · Manual billing · No card required</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 text-xl transition-colors">✕</button>
        </div>

        {/* Scrollable body */}
        <form onSubmit={onSubmit} className="overflow-y-auto flex-1 px-8 py-6 space-y-8">

          {/* ── Section 1: Company Details ── */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-5 pb-3 border-b-2 border-gray-200">
              Company Details
            </h3>
            <div className="space-y-4">
              <div>
                <label className={lbl}>Company Name *</label>
                <input required placeholder="e.g. TalentBeacon HR Solutions" value={form.company_name}
                  onChange={e => setForm(f => ({...f, company_name: e.target.value}))} className={inp} />
              </div>
              <div>
                <label className={lbl}>Address Line 1</label>
                <input placeholder="Street address" value={form.address_line1 || ''}
                  onChange={e => setForm(f => ({...f, address_line1: e.target.value}))} className={inp} />
              </div>
              <div>
                <label className={lbl}>Address Line 2 <span className="text-xs text-gray-400 font-normal">(Suite, Floor — optional)</span></label>
                <input placeholder="Suite 100, Floor 3" value={form.address_line2 || ''}
                  onChange={e => setForm(f => ({...f, address_line2: e.target.value}))} className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>City</label>
                  <input placeholder="City" value={form.city || ''}
                    onChange={e => setForm(f => ({...f, city: e.target.value}))} className={inp} />
                </div>
                <div>
                  <label className={lbl}>State / Province</label>
                  <input placeholder="State / Province" value={form.state_province || ''}
                    onChange={e => setForm(f => ({...f, state_province: e.target.value}))} className={inp} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Postal / ZIP Code</label>
                  <input placeholder="e.g. 10001" value={form.postal_code || ''}
                    onChange={e => setForm(f => ({...f, postal_code: e.target.value}))} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Country</label>
                  <input placeholder="e.g. United States" value={form.country || ''}
                    onChange={e => setForm(f => ({...f, country: e.target.value}))} className={inp} />
                </div>
              </div>
              <div>
                <label className={lbl}>Tax Registration Number (EIN/VAT) *</label>
                <input required placeholder="e.g. 12-3456789" value={form.tax_registration_number || ''}
                  onChange={e => setForm(f => ({...f, tax_registration_number: e.target.value}))} className={inp} />
              </div>
            </div>
          </div>

          {/* ── Section 2: Super Admin Account ── */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-5 pb-3 border-b-2 border-gray-200">
              Super Admin Account
            </h3>
            <div className="space-y-4">
              <div>
                <label className={lbl}>Full Name *</label>
                <input required placeholder="e.g. Vikranth Benedict" value={form.admin_full_name}
                  onChange={e => setForm(f => ({...f, admin_full_name: e.target.value}))} className={inp} />
              </div>
              <div>
                <label className={lbl}>Email Address *</label>
                <input required type="email" placeholder="e.g. admin@company.com" value={form.admin_email}
                  onChange={e => setForm(f => ({...f, admin_email: e.target.value}))} className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Username *</label>
                  <input required placeholder="e.g. talentadmin" value={form.admin_username}
                    onChange={e => setForm(f => ({...f, admin_username: e.target.value}))} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Password *</label>
                  <input required type="password" placeholder="Min 8 characters" value={form.admin_password}
                    onChange={e => setForm(f => ({...f, admin_password: e.target.value}))} className={inp} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 3: Invoice Rates ── */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1 pb-3 border-b-2 border-gray-200">
              Negotiated Invoice Rates
            </h3>
            <p className="text-sm text-gray-400 mb-5">Enter the agreed $ rate per usage for each phase. Leave blank if a phase is not in scope.</p>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">Phase 1</p>
                <p className="text-xs text-blue-500 mb-3">Per JD / Requirement</p>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400 text-sm font-bold">$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00" value={form.invoice_phase1_rate}
                    onChange={e => setForm(f => ({...f, invoice_phase1_rate: e.target.value}))}
                    className="w-full pl-7 pr-3 py-2.5 border-2 border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 text-sm bg-white" />
                </div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-1">Phase 2</p>
                <p className="text-xs text-purple-500 mb-3">Per Intake Call</p>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400 text-sm font-bold">$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00" value={form.invoice_phase2_rate}
                    onChange={e => setForm(f => ({...f, invoice_phase2_rate: e.target.value}))}
                    className="w-full pl-7 pr-3 py-2.5 border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 text-sm bg-white" />
                </div>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">Phase 3</p>
                <p className="text-xs text-green-500 mb-3">Per AI Interview</p>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400 text-sm font-bold">$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00" value={form.invoice_phase3_rate}
                    onChange={e => setForm(f => ({...f, invoice_phase3_rate: e.target.value}))}
                    className="w-full pl-7 pr-3 py-2.5 border-2 border-green-200 rounded-lg focus:outline-none focus:border-green-500 text-sm bg-white" />
                </div>
              </div>
            </div>
            <div>
              <label className={lbl}>Invoice Contact Email <span className="text-xs text-gray-400 font-normal">(if different from admin email)</span></label>
              <input type="email" placeholder="finance@company.com" value={form.invoice_contact_email}
                onChange={e => setForm(f => ({...f, invoice_contact_email: e.target.value}))} className={inp2} />
            </div>
            <div className="mt-4">
              <label className={lbl}>Agreement Notes <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
              <textarea placeholder="e.g. 12-month agreement signed May 2026. 30-day payment terms." rows={3}
                value={form.invoice_notes}
                onChange={e => setForm(f => ({...f, invoice_notes: e.target.value}))}
                className={inp2 + " resize-none"} />
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`px-4 py-3 rounded-xl text-sm font-medium ${
              message.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>{message}</div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <p className="text-xs text-gray-400">A welcome email with login credentials will be sent to the admin email.</p>
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl transition-all border border-gray-300">
              Cancel
            </button>
            <button type="submit" form="invoice-form" disabled={loading}
              onClick={onSubmit}
              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-all">
              {loading ? 'Creating...' : '🏢 Create & Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Create Support User Modal ────────────────────────────────────────────────
function CreateSupportModal({ show, onClose, form, setForm, onSubmit, loading, message }) {
  if (!show) return null;
  const inp = "w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50";
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Create Support User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-3">
          <input required placeholder="Full Name *" value={form.full_name}
            onChange={e => setForm(f => ({...f, full_name: e.target.value}))} className={inp} />
          <input required type="email" placeholder="Email *" value={form.email}
            onChange={e => setForm(f => ({...f, email: e.target.value}))} className={inp} />
          <input required placeholder="Username *" value={form.username}
            onChange={e => setForm(f => ({...f, username: e.target.value}))} className={inp} />
          <input required type="password" placeholder="Password *" value={form.password}
            onChange={e => setForm(f => ({...f, password: e.target.value}))} className={inp} />
          {message && (
            <p className={`text-sm font-medium ${message.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-900 disabled:opacity-50 text-white text-sm font-bold rounded-xl">
              {loading ? 'Creating...' : 'Create Support User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MasterAdminDashboard;